import * as signalR from "@microsoft/signalr";
import {
  getSignalRUserId,
  isAdminUser,
  isCustomerUser,
  usesSignalRNotifications,
} from "../utils/jwtRole";
import { getNotificationHubUrl } from "../utils/apiBaseUrl";
import {
  getCustomerGroupName,
  getParcelBookingCreatedGroupName,
  NEW_BOOKING_CREATED_EVENT,
  PARCEL_STATUS_UPDATE_EVENT,
  PARCEL_BOOKING_CREATED_GROUP,
  PARCEL_BOOKING_CREATED_GROUP_PREFIX,
  CUSTOMER_GROUP_PREFIX,
  SIGNALR_JOIN_GROUP_METHOD,
  SIGNALR_LEAVE_GROUP_METHOD,
} from "../types/parcelBookingSignalR.types";
import { SIGNALR_STATUS } from "../types/signalRConnection.types";

function getAccessToken() {
  return (
    localStorage.getItem("sundorbonToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("sundorbonToken") ||
    ""
  );
}

function isDev() {
  return process.env.NODE_ENV === "development";
}

function logSignalR(...args) {
  if (isDev()) {
    console.info("[SignalR]", ...args);
  }
}

function warnSignalR(...args) {
  console.warn("[SignalR]", ...args);
}

/** Backoff delays (ms) for manual reconnect after error / closed connection. */
const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000, 20000, 30000];

class SignalRService {
  constructor() {
    /** @type {signalR.HubConnection | null} */
    this.connection = null;
    /** @type {Map<string, Set<Function>>} */
    this.handlers = new Map();
    /** @type {Map<string, Function>} */
    this.dispatchers = new Map();
    /** @type {Promise<void> | null} */
    this.startPromise = null;
    /** @type {Set<string>} */
    this.joinedGroups = new Set();
    /** @type {Promise<void> | null} */
    this.syncGroupsPromise = null;
    /** @type {string | null} */
    this.activeHubUrl = null;
    /** @type {import('../types/signalRConnection.types').SignalRConnectionStatus} */
    this.connectionStatus = SIGNALR_STATUS.IDLE;
    /** @type {Set<(status: import('../types/signalRConnection.types').SignalRConnectionStatus) => void>} */
    this.statusListeners = new Set();
    this.reconnectEnabled = true;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.reconnectTimer = null;
    this.reconnectAttempt = 0;
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  getReconnectDelayMs() {
    const index = Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1);
    return RECONNECT_DELAYS_MS[index];
  }

  /**
   * Schedule a manual reconnect (used when start fails or connection closes).
   * @param {string} [reason]
   */
  scheduleReconnect(reason = "unknown") {
    if (!this.reconnectEnabled) return;
    if (!getAccessToken() || !usesSignalRNotifications()) return;

    const { HubConnectionState } = signalR;
    const state = this.connection?.state;

    if (
      state === HubConnectionState.Connected ||
      state === HubConnectionState.Connecting ||
      state === HubConnectionState.Reconnecting
    ) {
      return;
    }

    if (this.reconnectTimer || this.startPromise) {
      return;
    }

    const delay = this.getReconnectDelayMs();
    this.reconnectAttempt += 1;

    logSignalR(
      `Reconnect scheduled in ${delay}ms (attempt ${this.reconnectAttempt})`,
      reason,
    );
    this.setConnectionStatus(SIGNALR_STATUS.RECONNECTING);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.attemptReconnect();
    }, delay);
  }

  async attemptReconnect() {
    if (!this.reconnectEnabled || !getAccessToken() || !usesSignalRNotifications()) {
      return;
    }

    const { HubConnectionState } = signalR;

    if (this.connection?.state === HubConnectionState.Connected) {
      this.reconnectAttempt = 0;
      this.setConnectionStatus(SIGNALR_STATUS.CONNECTED);
      return;
    }

    try {
      await this.start();
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
    } catch {
      this.scheduleReconnect("reconnect attempt failed");
    }
  }

  /**
   * @param {import('../types/signalRConnection.types').SignalRConnectionStatus} status
   */
  setConnectionStatus(status) {
    if (this.connectionStatus === status) return;
    this.connectionStatus = status;
    this.statusListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (err) {
        console.error("[SignalR] Status listener error", err);
      }
    });
  }

  /** @returns {import('../types/signalRConnection.types').SignalRConnectionStatus} */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * @param {(status: import('../types/signalRConnection.types').SignalRConnectionStatus) => void} callback
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);
    callback(this.connectionStatus);
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  syncStatusFromHubState() {
    const connection = this.connection;
    if (!connection) {
      this.setConnectionStatus(SIGNALR_STATUS.DISCONNECTED);
      return;
    }

    const { HubConnectionState } = signalR;
    switch (connection.state) {
      case HubConnectionState.Connected:
        this.setConnectionStatus(SIGNALR_STATUS.CONNECTED);
        break;
      case HubConnectionState.Connecting:
        this.setConnectionStatus(SIGNALR_STATUS.CONNECTING);
        break;
      case HubConnectionState.Reconnecting:
        this.setConnectionStatus(SIGNALR_STATUS.RECONNECTING);
        break;
      case HubConnectionState.Disconnecting:
        this.setConnectionStatus(SIGNALR_STATUS.DISCONNECTED);
        break;
      default:
        this.setConnectionStatus(SIGNALR_STATUS.DISCONNECTED);
    }
  }

  buildConnection() {
    const hubUrl = getNotificationHubUrl();

    if (!hubUrl) {
      throw new Error(
        "SignalR hub URL is not configured. Set REACT_APP_API_URL in .env",
      );
    }

    this.activeHubUrl = hubUrl;

    if (isDev()) {
      console.info("[SignalR] Connecting to API hub:", hubUrl);
    }

    return new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
        accessTokenFactory: () => getAccessToken(),
        // Required when API is another origin (e.g. :7202 vs React :3000)
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
      .configureLogging(
        isDev() ? signalR.LogLevel.Information : signalR.LogLevel.Warning,
      )
      .build();
  }

  ensureConnection() {
    const hubUrl = getNotificationHubUrl();
    if (this.connection && this.activeHubUrl && this.activeHubUrl !== hubUrl) {
      this.connection.stop().catch(() => {});
      this.connection = null;
      this.startPromise = null;
      this.joinedGroups.clear();
    }

    if (!this.connection) {
      const built = this.buildConnection();
      this.connection = built;
      this.connection.onreconnecting((err) => {
        warnSignalR("Reconnecting…", err);
        this.setConnectionStatus(SIGNALR_STATUS.RECONNECTING);
      });
      this.connection.onreconnected((connectionId) => {
        logSignalR("Reconnected", connectionId);
        this.reconnectAttempt = 0;
        this.clearReconnectTimer();
        this.joinedGroups.clear();
        this.setConnectionStatus(SIGNALR_STATUS.CONNECTED);
        this.reregisterHubEvents();
        this.syncRoleGroups().catch((err) => {
          warnSignalR("Rejoin groups failed", err);
        });
      });
      this.connection.onclose((err) => {
        warnSignalR("Connection closed", err);
        this.joinedGroups.clear();
        this.setConnectionStatus(
          err ? SIGNALR_STATUS.ERROR : SIGNALR_STATUS.DISCONNECTED,
        );
        this.scheduleReconnect(err ? "closed with error" : "closed");
      });
    }
    return this.connection;
  }

  reregisterHubEvents() {
    const connection = this.connection;
    if (!connection) return;

    this.dispatchers.forEach((dispatcher, event) => {
      connection.off(event, dispatcher);
      connection.on(event, dispatcher);
    });
  }

  registerHubEvent(event) {
    const connection = this.ensureConnection();
    if (this.dispatchers.has(event)) return;

    const dispatcher = (...args) => {
      logSignalR("Event received:", event, args);
      const callbacks = this.handlers.get(event);
      if (!callbacks?.size) {
        warnSignalR("No handlers registered for", event);
        return;
      }
      callbacks.forEach((cb) => {
        try {
          cb(...args);
        } catch (err) {
          console.error(`[SignalR] Handler error (${event})`, err);
        }
      });
    };

    this.dispatchers.set(event, dispatcher);
    connection.on(event, dispatcher);
  }

  /**
   * @param {string} event
   * @param {(...args: unknown[]) => void} callback
   */
  on(event, callback) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event).add(callback);
    this.registerHubEvent(event);
  }

  /**
   * @param {string} event
   * @param {(...args: unknown[]) => void} callback
   */
  off(event, callback) {
    const set = this.handlers.get(event);
    if (!set) return;

    set.delete(callback);
    if (set.size > 0) return;

    const dispatcher = this.dispatchers.get(event);
    if (this.connection && dispatcher) {
      this.connection.off(event, dispatcher);
    }
    this.handlers.delete(event);
    this.dispatchers.delete(event);
  }

  /**
   * @param {string} groupName
   */
  async joinGroup(groupName) {
    const connection = this.ensureConnection();
    if (connection.state !== signalR.HubConnectionState.Connected) {
      warnSignalR("Cannot join group — not connected:", groupName);
      return false;
    }
    if (this.joinedGroups.has(groupName)) {
      return true;
    }

    try {
      await connection.invoke(SIGNALR_JOIN_GROUP_METHOD, groupName);
      this.joinedGroups.add(groupName);
      logSignalR("Joined group", groupName);
      return true;
    } catch (err) {
      warnSignalR(
        `Failed to invoke ${SIGNALR_JOIN_GROUP_METHOD}("${groupName}") — is this hub method defined on the backend?`,
        err,
      );
      return false;
    }
  }

  /**
   * @param {string} groupName
   */
  async leaveGroup(groupName) {
    const connection = this.ensureConnection();
    if (connection.state !== signalR.HubConnectionState.Connected) {
      this.joinedGroups.delete(groupName);
      return;
    }
    if (!this.joinedGroups.has(groupName)) {
      return;
    }

    try {
      await connection.invoke(SIGNALR_LEAVE_GROUP_METHOD, groupName);
    } catch (err) {
      warnSignalR("Leave group failed", groupName, err);
    } finally {
      this.joinedGroups.delete(groupName);
      logSignalR("Left group", groupName);
    }
  }

  async leaveParcelBookingGroups(exceptGroupName = "") {
    const stale = [...this.joinedGroups].filter(
      (name) =>
        name === PARCEL_BOOKING_CREATED_GROUP ||
        (name.startsWith(PARCEL_BOOKING_CREATED_GROUP_PREFIX) &&
          name !== exceptGroupName),
    );
    for (const name of stale) {
      await this.leaveGroup(name);
    }
  }

  async leaveCustomerGroups(exceptGroupName = "") {
    const stale = [...this.joinedGroups].filter(
      (name) =>
        name.startsWith(CUSTOMER_GROUP_PREFIX) && name !== exceptGroupName,
    );
    for (const name of stale) {
      await this.leaveGroup(name);
    }
  }

  /**
   * Admin → ParcelBookingCreated_{UserId}
   * User  → CustomerGroup_{UserId}
   */
  async syncRoleGroups() {
    if (this.syncGroupsPromise) {
      return this.syncGroupsPromise;
    }

    this.syncGroupsPromise = (async () => {
      const connection = this.ensureConnection();
      if (connection.state !== signalR.HubConnectionState.Connected) {
        return;
      }

      const userId = getSignalRUserId();
      const isAdmin = isAdminUser();
      const isCustomer = isCustomerUser();

      if (isAdmin && userId) {
        const targetGroup = getParcelBookingCreatedGroupName(userId);
        logSignalR("syncRoleGroups — admin group:", targetGroup);
        await this.leaveCustomerGroups();
        await this.leaveParcelBookingGroups(targetGroup);
        const joined = await this.joinGroup(targetGroup);
        if (!joined) {
          warnSignalR(
            "Connected but not in admin group — JoinGroup may have failed.",
            targetGroup,
          );
        }
        return;
      }

      if (isCustomer && userId) {
        const targetGroup = getCustomerGroupName(userId);
        logSignalR("syncRoleGroups — customer group:", targetGroup);
        await this.leaveParcelBookingGroups();
        await this.leaveCustomerGroups(targetGroup);
        const joined = await this.joinGroup(targetGroup);
        if (!joined) {
          warnSignalR(
            "Connected but not in customer group — JoinGroup may have failed.",
            targetGroup,
          );
        }
        return;
      }

      await this.leaveParcelBookingGroups();
      await this.leaveCustomerGroups();
    })()
      .catch((err) => {
        warnSignalR("syncRoleGroups failed", err);
        throw err;
      })
      .finally(() => {
        this.syncGroupsPromise = null;
      });

    return this.syncGroupsPromise;
  }

  async start() {
    if (!getAccessToken()) {
      logSignalR("Skipping start — no auth token");
      this.setConnectionStatus(SIGNALR_STATUS.IDLE);
      return;
    }

    this.reconnectEnabled = true;

    const connection = this.ensureConnection();
    const { HubConnectionState } = signalR;

    if (connection.state === HubConnectionState.Connected) {
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.setConnectionStatus(SIGNALR_STATUS.CONNECTED);
      await this.syncRoleGroups().catch(() => {});
      return;
    }

    if (
      connection.state === HubConnectionState.Connecting ||
      connection.state === HubConnectionState.Reconnecting
    ) {
      this.syncStatusFromHubState();
      return this.startPromise ?? Promise.resolve();
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    this.setConnectionStatus(SIGNALR_STATUS.CONNECTING);

    this.startPromise = connection
      .start()
      .then(async () => {
        logSignalR("Connected to", getNotificationHubUrl());
        this.reconnectAttempt = 0;
        this.clearReconnectTimer();
        this.setConnectionStatus(SIGNALR_STATUS.CONNECTED);
        this.reregisterHubEvents();
        await this.syncRoleGroups();
      })
      .catch((err) => {
        warnSignalR("Failed to connect to", getNotificationHubUrl(), err);
        this.setConnectionStatus(SIGNALR_STATUS.ERROR);
        this.scheduleReconnect("start failed");
        throw err;
      })
      .finally(() => {
        this.startPromise = null;
      });

    return this.startPromise;
  }

  async stop() {
    this.reconnectEnabled = false;
    this.clearReconnectTimer();
    this.reconnectAttempt = 0;
    this.startPromise = null;
    this.syncGroupsPromise = null;
    this.joinedGroups.clear();
    if (!this.connection) {
      this.setConnectionStatus(SIGNALR_STATUS.IDLE);
      return;
    }

    try {
      await this.connection.stop();
      logSignalR("Stopped");
      this.setConnectionStatus(SIGNALR_STATUS.DISCONNECTED);
    } catch (err) {
      warnSignalR("Stop failed", err);
      this.setConnectionStatus(SIGNALR_STATUS.ERROR);
    }
  }

  getState() {
    return this.connection?.state ?? signalR.HubConnectionState.Disconnected;
  }

  /** Ensure default booking event listener is registered on the hub connection. */
  ensureBookingListenerRegistered() {
    this.registerHubEvent(NEW_BOOKING_CREATED_EVENT);
    this.registerHubEvent(PARCEL_STATUS_UPDATE_EVENT);
  }
}

let singleton = null;

export function getSignalRService() {
  if (!singleton) {
    singleton = new SignalRService();
  }
  return singleton;
}

export { getNotificationHubUrl };

export default getSignalRService;
